import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../ui/button';
import { HeartHandshake, ArrowRight, Sparkles, RefreshCw } from 'lucide-react';

/**
 * Compact Care Profile / Caregiver Style card designed to live inside the
 * Resource Hub. If the user has completed the assessment we surface their
 * primary Care Profile + Caregiver Style names with a "View full profile"
 * button. Otherwise we show a friendly CTA to take the quiz.
 */
const join = (arr, key = 'name') => (arr || []).map((x) => x[key]).join(' & ');

const CareProfileResourceCard = () => {
  const navigate = useNavigate();
  const [state, setState] = useState('loading'); // loading | new | resume | done
  const [primary, setPrimary] = useState('');
  const [secondary, setSecondary] = useState('');
  const [caregiverStyle, setCaregiverStyle] = useState('');
  const [caregiverDescription, setCaregiverDescription] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const prog = await axios.get(`${API}/assessments/care_profile/progress`);
        if (cancelled) return;
        if (prog.data.has_results) {
          const r = await axios.get(`${API}/assessments/care_profile/results`);
          if (cancelled) return;
          const m = r.data?.scores?.metrics || {};
          setPrimary(join(m.care_profile?.primary));
          setSecondary(join(m.care_profile?.secondary));
          setCaregiverStyle(join(m.caregiver_style?.primary));
          setCaregiverDescription(m.caregiver_style?.primary?.[0]?.description || '');
          setState('done');
        } else {
          setState(prog.data.progress ? 'resume' : 'new');
        }
      } catch (_e) {
        // Fail silent — this card is optional context.
        if (!cancelled) setState('new');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (state === 'loading') return null;

  if (state === 'done') {
    return (
      <section
        className="surface-card rounded-2xl p-5 border-l-4 border-cyan-500 space-y-3"
        data-testid="care-profile-resource-card"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-600 to-teal-600 flex items-center justify-center flex-shrink-0">
            <HeartHandshake className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-semibold text-cyan-700 dark:text-cyan-300 uppercase tracking-wider">Your Care Guide™</p>
            <h2 className="text-base font-bold text-foreground mt-0.5" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {primary || 'Care Profile ready'}
            </h2>
            {secondary && (
              <p className="text-xs text-muted-foreground mt-0.5">Secondary: {secondary}</p>
            )}
          </div>
        </div>

        {caregiverStyle && (
          <div className="rounded-xl bg-cyan-50/70 dark:bg-cyan-900/20 p-3">
            <p className="text-[10px] font-semibold text-cyan-700 dark:text-cyan-300 uppercase tracking-wider">Caregiver Style™</p>
            <p className="text-sm font-semibold text-foreground mt-0.5">{caregiverStyle}</p>
            {caregiverDescription && (
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{caregiverDescription}</p>
            )}
          </div>
        )}

        <div className="flex gap-2">
          <Button
            className="flex-1 btn-primary"
            onClick={() => navigate('/care-profile')}
            data-testid="care-profile-view-full-btn"
          >
            View full profile <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate('/care-profile')}
            aria-label="Retake"
            data-testid="care-profile-retake-shortcut"
            title="Retake"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </section>
    );
  }

  return (
    <section
      className="rounded-2xl p-5 bg-gradient-to-br from-cyan-600 to-teal-600 text-white shadow-md"
      data-testid="care-profile-resource-card-cta"
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <p className="text-[10px] font-semibold uppercase tracking-wider opacity-80">Care Guide™</p>
          <h2 className="text-base font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {state === 'resume' ? 'Finish your Care Profile' : 'Discover your Care Profile'}
          </h2>
          <p className="text-xs opacity-90 mt-0.5 leading-relaxed">
            {state === 'resume'
              ? "You're part-way there — pick up right where you left off."
              : '8–10 minutes to show your squad exactly how to support you.'}
          </p>
        </div>
      </div>
      <Button
        onClick={() => navigate('/care-profile')}
        variant="secondary"
        className="w-full mt-4 bg-white text-cyan-700 hover:bg-white/90"
        data-testid="care-profile-start-btn"
      >
        {state === 'resume' ? 'Resume' : 'Start the assessment'} <ArrowRight className="w-4 h-4 ml-1" />
      </Button>
    </section>
  );
};

export default CareProfileResourceCard;
